import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import clsx from 'clsx';

/**
 * Aviso de preço para a Black Friday do Mercado Livre.
 * Regra do ML: produto com aumento de preço nos últimos 60 dias fica fora da campanha,
 * e a tag Black Friday exige o menor preço dos últimos 60 dias.
 * O aviso some sozinho depois da Cyber Monday.
 */
const BF_FIM = new Date('2026-12-01T03:00:00Z'); // 01/12/2026 00:00 (Brasília)

export const blackFridayAtiva = () => Date.now() < BF_FIM.getTime();

/** true se a lista de marketplaces / canais inclui o Mercado Livre */
export const temMercadoLivre = (ids: Array<string | undefined | null> = []) =>
  ids.some(id => {
    const s = String(id || '').toLowerCase();
    return s === 'ml' || s.includes('mercado') || s.includes('meli');
  });

const TITULO = 'Black Friday Mercado Livre (27/11)';
const TEXTO =
  'Não aumente o preço deste produto até a Black Friday. O Mercado Livre deixa fora da campanha ' +
  'produtos com aumento de preço nos últimos 60 dias, e a tag Black Friday só aparece com o menor ' +
  'preço dos últimos 60 dias.';

interface Props {
  /** 'icon' = ícone com tooltip (hover/clique) · 'banner' = faixa fixa */
  variant?: 'icon' | 'banner';
  /** Preço subiu em relação ao salvo → destaque em vermelho */
  precoSubiu?: boolean;
  className?: string;
}

export default function BlackFridayPriceHint({ variant = 'icon', precoSubiu = false, className }: Props) {
  const [aberto, setAberto] = useState(false);
  if (!blackFridayAtiva()) return null;

  if (variant === 'banner') {
    return (
      <div
        className={clsx(
          'flex gap-3 p-3 rounded-lg border text-sm',
          precoSubiu
            ? 'border-red-300 bg-red-50 text-red-800'
            : 'border-amber-300 bg-amber-50 text-amber-900',
          className,
        )}
      >
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">{TITULO}</p>
          <p className="opacity-90">
            {precoSubiu && <strong>Você aumentou o preço. </strong>}
            {TEXTO}
          </p>
        </div>
      </div>
    );
  }

  return (
    <span
      className={clsx('relative inline-flex align-middle', className)}
      onMouseEnter={() => setAberto(true)}
      onMouseLeave={() => setAberto(false)}
    >
      <button
        type="button"
        aria-label={TITULO}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAberto(a => !a); }}
        onBlur={() => setAberto(false)}
        className={clsx(
          'p-0.5 rounded transition-colors',
          precoSubiu ? 'text-red-600 hover:text-red-700' : 'text-amber-600 hover:text-amber-700',
        )}
      >
        <AlertTriangle className="w-4 h-4" />
      </button>
      {aberto && (
        <span
          role="tooltip"
          className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 p-3 rounded-lg border border-amber-300 bg-white shadow-xl text-xs font-normal normal-case text-left text-gray-700 whitespace-normal"
        >
          <span className="block font-semibold text-amber-800 mb-1">{TITULO}</span>
          {precoSubiu && <span className="block font-semibold text-red-700 mb-1">Você aumentou o preço.</span>}
          {TEXTO}
        </span>
      )}
    </span>
  );
}
